import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from './useAuth';

export interface OptimizedQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  queryKey: unknown[];
  queryFn: () => Promise<T>;
  requiresAuth?: boolean;
}

export function useOptimizedQuery<T>({
  queryKey,
  queryFn,
  requiresAuth = false,
  ...options
}: OptimizedQueryOptions<T>): UseQueryResult<T> & { optimizedRefetch: () => Promise<any> } {
  const { user } = useAuth();

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: requiresAuth ? !!user && (options.enabled ?? true) : (options.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      if (error?.message?.includes('auth') || error?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });

  return {
    ...query,
    optimizedRefetch: () => query.refetch()
  };
}